import { doc, runTransaction } from 'firebase/firestore';
import { eduDb } from '@/firebase';
import { useAuthStore } from '@/v2/store/shared/auth-store';

/**
 * Service to manage stats counters in tenant document
 * Used to generate incremental IDs for collections similar to SQL auto-increment
 */
export class AutoIncrementService {
	/**
	 * Get the next ID for a specific collection
	 * @param {string} collectionName - The name of the collection
	 * @returns {Promise<number>} - The next ID to use
	 */
	static async getNextId(collectionName, db = eduDb) {
		const authStore = useAuthStore();
		const tenantId = `${authStore.tenantId}`;

		if (!tenantId) {
			throw new Error('Tenant ID is required');
		}

		const tenantDocRef = doc(db, 'tenants', tenantId);

		try {
			// Use transaction to ensure atomicity when incrementing counters
			return await runTransaction(db, async (transaction) => {
				const tenantDoc = await transaction.get(tenantDocRef);

				if (!tenantDoc.exists()) {
					throw new Error(`Tenant document not found: ${tenantId}`);
				}

				const tenantData = tenantDoc.data();
				const autoIncrementIds = tenantData.autoIncrementIds || {};
				const currentCounter = autoIncrementIds[collectionName] || 0;
				const nextId = currentCounter + 1;

				// Update the counter in the stats object
				transaction.update(tenantDocRef, {
					[`autoIncrementIds.${collectionName}`]: nextId,
				});

				return nextId;
			});
		} catch (error) {
			console.error('Error getting next ID:', error);
			throw error;
		}
	}
}
